import { useI18n, type LocaleCode } from "../../i18n/index.js";
import { usePreferences } from "../../lib/preferences.js";
import type { ThemeMode } from "../../theme/index.js";
import { PageHeader, Select, SettingsRow, SettingsSection, Skeleton } from "../../ui/index.js";

export function PreferencesSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("settings.preferences.title")} description={t("settings.preferences.description")} />
      <SettingsSection>
        <SettingsRow label={t("language.label")}>
          <Skeleton shape="select" />
        </SettingsRow>
        <SettingsRow label={t("appearance.label")} description={t("settings.preferences.appearanceHint")}>
          <Skeleton shape="select" />
        </SettingsRow>
      </SettingsSection>
    </>
  );
}

/**
 * Settings: personal preferences. Both apply at once and are saved to the account, so they
 * follow the person to every device — the same settings the account menu offers.
 */
export function PreferencesRoute() {
  const { t, languages } = useI18n();
  const preferences = usePreferences({ announce: true });
  const modes = (["system", "light", "dark"] as const).map((value) => ({ value, label: t(`appearance.${value}`) }));
  return (
    <>
      <PageHeader title={t("settings.preferences.title")} description={t("settings.preferences.description")} />
      <SettingsSection>
        <SettingsRow label={t("language.label")}>
          <Select
            items={languages.map((l) => ({ value: l.code, label: l.nativeLabel }))}
            value={preferences.locale}
            onValueChange={(v) => preferences.setLanguage(v as LocaleCode)}
            aria-label={t("language.label")}
          />
        </SettingsRow>
        <SettingsRow label={t("appearance.label")} description={t("settings.preferences.appearanceHint")}>
          <Select items={modes} value={preferences.appearance} onValueChange={(v) => preferences.setAppearance(v as ThemeMode)} aria-label={t("appearance.label")} />
        </SettingsRow>
      </SettingsSection>
    </>
  );
}
